import React, { createContext, useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RegisterDevice, DeRegisterDevice } from '../services/auth';
import {
  getStaffUserName,
  getRegisterDevice,
  setIdentificationId,
  setRegisterDevice,
  setPortalServerIp,
  getPortalServerIp,
  setStaffUserName,
  getE2EEncryptionEnabledFlag,
} from '../storage';
import { useToast } from './toast';
import { setPresenceStateLogin } from '../services/presenceState';

import { useIntl } from 'react-intl';
import { SetEncryptionKey } from '../services/e2ee';
import { closeSSEConnection } from '../services/sseManager';

import { clearDecryptedConversationKeyCache } from '../services/conversationKeyCache';
import { setE2EEncryptionEnabled } from '../services/e2eeSettings';
import { clearDecryptedMediaCache } from '../components/balloon/EncryptedMedia';
import publicIp from 'public-ip';
import { unsubscribeUserFromPush } from '../swRegistration';
import signalRService from '../services/SignalRService';

const AuthContext = createContext();
const AuthProvider = ({ children }) => {
  const intl = useIntl();

  const history = useHistory();
  const [user, setUser] = useState('');
  const [portal, setPortal] = useState(getPortalServerIp());
  const [identificationId, setIdentification] = useState('');
  const { addToast } = useToast();
  const [data, setData] = useState(() => {
    const registerDevice = getRegisterDevice();
    const staffUserName = getStaffUserName();
    if (registerDevice && staffUserName) {
      setIdentificationId({
        IdentificationId:
          registerDevice.RegisterDeviceResult.Configuration.IdentificationId,
      });
      setRegisterDevice({
        RegisterDevice: JSON.stringify(registerDevice),
      });
      setUser(registerDevice.RegisterDeviceResult.Configuration.DisplayName);
      setIdentification(
        registerDevice.RegisterDeviceResult.Configuration.IdentificationId
      );
      setPortalServerIp({
        PortalServerIp:
          registerDevice.RegisterDeviceResult.Configuration.PortalServerIp,
      });
      setPortal(
        registerDevice.RegisterDeviceResult.Configuration.PortalServerIp
      );
      // history.push('/app');
      return { registerDevice };
    }
    return null;
  });

  const signIn = useCallback(
    async ({ userName, password, IpAddress, rsaKeys }) => {
      try {
        const registerDevice = await RegisterDevice({
          userName,
          password,
          IpAddress,
        });
        // console.log(registerDevice);
        if (registerDevice.RegisterDeviceResult.RegistrationRequired) {
          addToast({
            title: intl.formatMessage({ id: 'AuthenticationError' }),
            description: intl.formatMessage({
              id: 'UserNotFoundCheckYourDataAndTryAgain',
            }),
            type: 'error',
          });
        } else {
          setPortalServerIp({
            PortalServerIp:
              registerDevice.RegisterDeviceResult.Configuration.PortalServerIp,
          });

          await setE2EEncryptionEnabled(
            registerDevice.RegisterDeviceResult.Configuration.IdentificationId
          );

          if (rsaKeys && getE2EEncryptionEnabledFlag()) {
            const identificationId =
              registerDevice.RegisterDeviceResult.Configuration
                .IdentificationId;
            localStorage.setItem(
              `privateRSAKey-${identificationId}`,
              rsaKeys.private
            );

            // Store in IndexedDB so the Service Worker can access it
            try {
              const dbRequest = indexedDB.open('E2EEStore', 1);
              dbRequest.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains('keys')) {
                  db.createObjectStore('keys');
                }
              };
              dbRequest.onsuccess = (e) => {
                const db = e.target.result;
                if (db.objectStoreNames.contains('keys')) {
                  const tx = db.transaction('keys', 'readwrite');
                  const store = tx.objectStore('keys');
                  store.put(
                    rsaKeys.private,
                    `privateRSAKey-${identificationId}`
                  );
                  store.put(identificationId, 'activeIdentificationId');
                }
              };
            } catch (indexedDbError) {
              console.error(
                'Failed to save private key to IndexedDB:',
                indexedDbError
              );
            }

            try {
              await SetEncryptionKey(rsaKeys.public, identificationId);
            } catch (encryptionError) {
              console.error('Encryption key setup failed:', encryptionError);
              return; // prevent further processing if encryption key setup fails
            }
          }

          setPortal(
            registerDevice.RegisterDeviceResult.Configuration.PortalServerIp
          );
          setIdentificationId({
            IdentificationId:
              registerDevice.RegisterDeviceResult.Configuration
                .IdentificationId,
          });
          setRegisterDevice({
            RegisterDevice: JSON.stringify(registerDevice),
          });
          setUser(
            registerDevice.RegisterDeviceResult.Configuration.DisplayName
          );
          setIdentification(
            registerDevice.RegisterDeviceResult.Configuration.IdentificationId
          );
          setStaffUserName(userName);
          history.push('/app');
          setPresenceStateLogin();
        }
      } catch (error) {
        console.error(error);
      }
    },
    [addToast, history]
  );

  const logout = useCallback(async () => {
    let ip = '';
    try {
      ip = await publicIp.v4();
    } catch (error) {
      ip = '';
    }

    // 1. Unsubscribe from browser Web Push
    try {
      await unsubscribeUserFromPush();
    } catch (err) {
      console.error('[Logout] Failed to unsubscribe from push:', err);
    }

    // 2. Call DeRegisterDevice while data is still in localStorage
    try {
      await DeRegisterDevice({ ip });
    } catch (err) {
      console.error('[Logout] Failed to deregister device:', err);
    }

    // 3. Clear cache and local storage (await the async media cache cleanup)
    try {
      await clearDecryptedMediaCache();
      await signalRService.logout();
      // Preserve chosen language across logout
      const savedLanguage =
        localStorage.getItem('app_language') ||
        localStorage.getItem('@Squre:language');
      localStorage.clear();
      if (savedLanguage) {
        localStorage.setItem('app_language', savedLanguage);
        localStorage.setItem('@Squre:language', savedLanguage);
      }
      sessionStorage.clear();
      closeSSEConnection();
      clearDecryptedConversationKeyCache();
    } catch (err) {
      console.error('[Logout] Error during storage or cache cleanup:', err);
    }

    // 4. Clear key store in IndexedDB (using version 1)
    try {
      const dbRequest = indexedDB.open('E2EEStore', 1);
      dbRequest.onsuccess = (e) => {
        const db = e.target.result;
        if (db.objectStoreNames.contains('keys')) {
          const tx = db.transaction('keys', 'readwrite');
          tx.objectStore('keys').clear();
          console.log('[E2EE-Auth] Cleared IndexedDB key store on logout');
        }
      };
    } catch (dbError) {
      console.error('Failed to clear IndexedDB keys:', dbError);
    }

    // 5. Cleanly reset all React Auth Context states
    setData(null);
    setUser('');
    setIdentification('');
    setPortal(null);

    // 6. Navigate to Login
    history.push('/');
  }, [history, setPortal]);

  return (
    <AuthContext.Provider
      value={{
        registerDevice: data,
        user,
        signIn,
        identificationId,
        portal,
        setPortal,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}
export { AuthProvider, AuthContext, useAuth };
